// SAFE-WRITE-BEGIN: frontend/src/pages/valuation/_valueMethods.ts
/**
 * M-DEMO — the pure pieces behind Compare values and the 10-year Forecast:
 * the owner-approved text (MD-1..MD-12), the five method columns (MD-3b), the
 * status sentence for a method that shows no value, and the client-side
 * compounding the forecast redraws from while the slider moves.
 *
 * SCREEN ONLY. Nothing here reaches the Snapshot, a report or the signed record.
 */
import type { MethodEntry, MethodKey } from '@/types/valuationB3'

export const GROWTH_MIN = -20
export const GROWTH_MAX = 50
export const GROWTH_STEP = 0.5
/** Conservative / Strong sit this many points either side of the chosen rate. */
export const SCENARIO_SPREAD = 3

/** Owner-approved text, word for word. Invent nothing beside it. */
export const MD = {
  title: 'Compare values', // MD-1
  note: 'Your value above is the headline. These are other ways to look at the same business, for comparison.', // MD-2
  cash: 'Cash in the business', // MD-4
  debt: 'Loans and other interest-bearing debt',
  growthBox: 'Yearly growth rate (%)',
  forecastTitle: '10-year Forecast', // MD-5
  forecastNote: 'Grows the value at the rate you choose each year. It is an illustration, not a promise.',
  signerReview: 'A valuation signer reviews this one.', // MD-6
  needsMore: (what: string) => `Needs more information: ${what}.`, // MD-7
  enterCashDebt: 'Enter cash and debt to see this.', // MD-8
  growthOutOfRange: 'Enter a yearly growth rate between -20% and 50%.', // MD-9
  tileToday: 'Value today', // MD-10
  tileYear10: (rate: number) => `Value in year 10 at ${rate}%/yr`,
  tileGrowth: 'Growth in value over 10 years',
  expected: 'Expected', // MD-11
  conservative: 'Conservative',
  strong: 'Strong',
  band: 'Shaded band: the range between the conservative and strong rates.',
  slider: 'Yearly growth rate', // MD-12
  today: 'Today', // mockup wording
  year: (n: number) => `Year ${n}`,
} as const

export interface MethodCard {
  key: MethodKey
  name: string
  subtitle: string
  color: string
}

/** MD-3b — one column per method, in this order. No column is ever red. */
export const METHOD_CARDS: readonly MethodCard[] = [
  { key: 'sde', name: 'Owner earnings value', subtitle: 'SDE', color: '#3b6fb6' },
  { key: 'ebitda', name: 'Operating profit value', subtitle: 'EBITDA', color: '#7c3aed' },
  { key: 'asset', name: 'Net asset value', subtitle: 'Assets less debts', color: '#2f8f83' },
  { key: 'enterprise', name: 'Enterprise value', subtitle: 'Whole business', color: '#c9a24a' },
  { key: 'equity', name: 'Equity value', subtitle: "Owner's share", color: '#ea580c' },
]

// MD-7 <what is missing> phrases — owner-approved (OWNER_APPROVALS.log 2026-10-25 15:57).
const NEEDS: Record<string, string> = {
  needs_two_years: 'two years of figures',
  needs_questionnaire: 'the questionnaire answers',
  needs_balance_sheet: 'a balance sheet',
  needs_calculated_value: 'the calculated value',
}

/** The grey sentence for a method; null when it is available (its value shows). */
export function methodSentence(m: MethodEntry): string | null {
  if (m.status === 'available') return null
  if (m.status === 'signer_review') return MD.signerReview
  if (m.reason.code === 'needs_cash_and_debt') return MD.enterCashDebt
  const what = NEEDS[m.reason.code]
  // An unmapped code drops the <what> part of MD-7 (owner-approved 15:57).
  return what ? MD.needsMore(what) : 'Needs more information.'
}

const cents = (x: number) => Math.round(x * 100 + (x >= 0 ? 1e-7 : -1e-7)) / 100

/** Today then years 1..10: value × (1 + rate/100)^n, rounded to cents. */
export function forecastSeries(value: number, rate: number): number[] {
  return Array.from({ length: 11 }, (_, n) => cents(value * (1 + rate / 100) ** n))
}

const clampRate = (r: number) => Math.min(GROWTH_MAX, Math.max(GROWTH_MIN, r))

export function scenarioRates(rate: number) {
  return {
    expected: rate,
    conservative: clampRate(rate - SCENARIO_SPREAD),
    strong: clampRate(rate + SCENARIO_SPREAD),
  }
}

export interface ForecastRow {
  label: string
  year: number
  expected: number
  conservative: number
  strong: number
  band: [number, number]
}

export function forecastChartData(today: number, rate: number): ForecastRow[] {
  const r = scenarioRates(rate)
  const exp = forecastSeries(today, r.expected)
  const lo = forecastSeries(today, r.conservative)
  const hi = forecastSeries(today, r.strong)
  return exp.map((v, year) => ({
    label: year === 0 ? MD.today : MD.year(year),
    year,
    expected: v,
    conservative: lo[year],
    strong: hi[year],
    band: [lo[year], hi[year]],
  }))
}

/** '$3.03M' — the forecast's own scale; money() stays the whole-dollar format. */
export function millions(v: number): string {
  const sign = v < 0 ? '-' : ''
  return `${sign}$${(Math.abs(v) / 1_000_000).toFixed(2)}M`
}

/** A typed rate inside −20..50, or null (MD-9). */
export function parseGrowth(raw: string): number | null {
  const s = raw.trim()
  if (s === '') return null
  const n = Number(s)
  if (!Number.isFinite(n) || n < GROWTH_MIN || n > GROWTH_MAX) return null
  return n
}

/** A stored draft value arrives stamped { value, by, at }; a fresh one is bare. */
export function storedNumber(v: unknown): number | null {
  if (typeof v === 'number') return v
  if (v && typeof v === 'object' && typeof (v as { value?: unknown }).value === 'number') {
    return (v as { value: number }).value
  }
  return null
}
// SAFE-WRITE-EOF: frontend/src/pages/valuation/_valueMethods.ts
